import { Directive, inject, Input, TemplateRef, ViewContainerRef } from "@angular/core";
import { PermissionService } from "../../../services/api/permission/permission.service";
import { PERMISSION } from "../../../enums/permissions.enum";

@Directive({
  selector: "[appHasPermission]",
  standalone: true,
})
export class HasPermissionDirective {
  private templateRef = inject(TemplateRef<any>);
  private permissionService = inject(PermissionService);
  private viewContainer = inject(ViewContainerRef);

  /**
   * Set the permissions that are required to render the content.
   * If the user does not have the required permissions, the content will not be rendered.
   * Optionally, a missing permission can be specified. If the user does not have the specified missing permission,
   * the content will be rendered.
   * @param conditions The conditions to check.
   * @param conditions.permissions The permissions that are required to render the content.
   * @param conditions.missingPermission The permission that the user should not have to render the content.
   */
  @Input() set appHasPermission(conditions: {
    permissions: PERMISSION[];
    missingPermission?: PERMISSION;
    needsAllPermissions?: boolean;
  }) {
    let hasPermission: boolean = conditions.needsAllPermissions ? true : false;

    // depending on needsAllPermissions either needs all permissions from permissions array or only one
    conditions.permissions.forEach((permission: PERMISSION) => {
      if (conditions.needsAllPermissions) {
        hasPermission = hasPermission && this.permissionService.hasPermission(permission);
      } else {
        hasPermission = hasPermission || this.permissionService.hasPermission(permission);
      }
    });

    let shouldRender = hasPermission;
    if (conditions.missingPermission) {
      const missesPermission: boolean = this.permissionService.missesPermission(conditions.missingPermission);
      // only render if the user does not have the missing permission
      shouldRender = shouldRender && missesPermission;
    }

    if (shouldRender) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else {
      this.viewContainer.clear();
    }
  }
}
